import React, { useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';

import { useClickOutside } from '../../hooks/useClickOutside';
import { useEscape } from '../../hooks/useEscape';
import { useKeyDown } from '../../hooks/useKeyDown';
import { theme } from '../../system/theme';
import { BasePaginateProps } from '../../types/BasePaginateProps';
import { BaseStatusTextProps } from '../../types/BaseStatusTextProps';
import { SharedIconProps } from '../../types/SharedIconProps';
import { UserAction } from '../../types/UserAction';
import { Actions } from '../Actions/Actions';
import { Backdrop } from '../Backdrop/Backdrop';
import { Box } from '../Box/Box';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { Portal } from '../Portal/Portal';
import { StatusText } from '../StatusText/StatusText';
import { Text } from '../Text/Text';

interface Props {
  /** aria-label name for accessibility */
  name: string;
  /** Lightbox title */
  title: string;
  /** Optional actions */
  actions?: UserAction[];
  /** Optional custom content, displayed below the actions and above the pagination */
  content?: React.ReactElement;
  /** Optional description */
  description?: string;
  /** Optional icon, displayed to the left of the title */
  icon?: SharedIconProps;
  /** If provided, enables "carousel" mode for browsing multiple lightboxes */
  paginate?: { label?: string } & BasePaginateProps;
  /** Optional statusText displayed to the left of the actions */
  statusText?: BaseStatusTextProps;
  /** Optional callback when the lightbox is closed */
  onHide?: () => void;
}

export const Lightbox = ({
  actions,
  content,
  description,
  icon,
  name,
  paginate,
  statusText,
  title,
  onHide,
}: Props) => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const overlayRef = useRef(null);

  const pageIndexDisplay = formatNumber(paginate?.pageIndex);
  const totalCountDisplay = formatNumber(paginate?.totalCount);

  const handleHide = () => {
    onHide?.();
  };

  const handleBack = () => {
    if (!paginate) return;
    if (paginate.pageIndex === 1) {
      paginate.onUpdatePageIndex(paginate.totalCount);
    } else {
      paginate.onUpdatePageIndex(paginate.pageIndex - 1);
    }
  };
  const handleNext = () => {
    if (!paginate) return;
    if (paginate.pageIndex === paginate.totalCount) {
      paginate.onUpdatePageIndex(1);
    } else {
      paginate.onUpdatePageIndex(paginate.pageIndex + 1);
    }
  };

  useClickOutside(overlayRef, handleHide);

  useEscape(handleHide, true);

  useKeyDown('ArrowLeft', handleBack, Boolean(paginate));
  useKeyDown('ArrowRight', handleNext, Boolean(paginate));

  return (
    <Portal>
      <Backdrop disableBodyScroll mode="center">
        <div ref={overlayRef} style={componentStyles.overlay}>
          <Layout
            aria-modal
            direction="column"
            name={name}
            role="dialog"
            styles={componentStyles.container}
            variant="borderRadius.all"
            h="lightbox.height"
            p={6}
          >
            <Layout justify="space-between" align="center" pb={6}>
              <Layout align="center" spacing={3}>
                {icon && <Icon {...icon} />}
                <Text variant="subtitle">{title}</Text>
              </Layout>
              <Actions
                actions={[
                  {
                    text: intl.formatMessage({
                      id: 'Lightbox.close',
                      defaultMessage: 'Close',
                      description:
                        'Tooltip text for the icon to close the lightbox',
                    }),
                    icon: 'x',
                    mode: 'icon',
                    onClick: handleHide,
                  },
                ]}
              />
            </Layout>
            <Layout direction="column" justify="space-between" h="100%">
              <Layout direction="column" spacing={6}>
                {description && <Text preset="description">{description}</Text>}
                <Layout justify={statusText ? 'space-between' : 'flex-end'}>
                  {statusText && <StatusText {...statusText} />}
                  {actions && <Actions justify="flex-end" actions={actions} />}
                </Layout>
                {content && (
                  <Box
                    maxH={360}
                    overflowY="auto"
                    border="border"
                    borderRadius={theme.radii.m}
                    p={4}
                  >
                    {content}
                  </Box>
                )}
              </Layout>
              {paginate && (
                <Layout justify="space-between">
                  <Actions
                    actions={[
                      {
                        icon: 'chevron-left',
                        iconPosition: 'left',
                        mode: 'chip',
                        status: 'info',
                        text: intl.formatMessage({
                          id: 'Lightbox.back',
                          defaultMessage: 'Back',
                          description: 'Button to go back to the previous item',
                        }),
                        onClick: handleBack,
                      },
                    ]}
                  />
                  <Layout direction="column" justify="center" align="center">
                    <Text variant="body-bold">{paginate.label}</Text>
                    <Text>
                      <FormattedMessage
                        id="Lightbox.paginateLabels"
                        defaultMessage="{pageIndexDisplay} of {totalCountDisplay}"
                        description="Pagination display counts"
                        values={{ pageIndexDisplay, totalCountDisplay }}
                      />
                    </Text>
                  </Layout>
                  <Actions
                    actions={[
                      {
                        icon: 'chevron-right',
                        mode: 'chip',
                        status: 'info',
                        text: intl.formatMessage({
                          id: 'Lightbox.next',
                          defaultMessage: 'Next',
                          description: 'Button to advance to the next item',
                        }),
                        onClick: handleNext,
                      },
                    ]}
                  />
                </Layout>
              )}
            </Layout>
          </Layout>
        </div>
      </Backdrop>
    </Portal>
  );
};

const offsetMargin = theme.spacings[12] as number;

const componentStyles = {
  container: {
    backgroundColor: 'background',
    boxShadow: 'raised',
    flex: 'auto',
    height: '100%',
    marginBottom: offsetMargin,
    marginTop: offsetMargin,
    minWidth: 0,
    width: '100%',
  },
  overlay: {
    display: 'flex',
    maxWidth: '100vw',
    width: theme.sizes['panel.width.l'],
  } as object,
};
